import React from "react";
import {
  Box,
  Button,
  Drawer,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import TextElementEditor from "./elementEditors/TextElementEditor";
import ButtonElementEditor from "./elementEditors/ButtonElementEditor";
import ImageElementEditor from "./elementEditors/ImageElementEditor";
import CollectionItemEditor from "./elementEditors/CollectionItemEditor";
import CollectionElementEditor from "./elementEditors/CollectionElementEditor";
import NavbarMobileElementEditor from "./elementEditors/NavbarMobileElementEditor";
import SectionShellEditor from "./elementEditors/SectionShellEditor";
import CardSystemEditor from "./elementEditors/CardSystemEditor";
import CategoriesElementEditor from "./elementEditors/CategoriesElementEditor";
import ContactElementEditor from "./elementEditors/ContactElementEditor";
import FooterElementEditor from "./elementEditors/FooterElementEditor";
import FloatingWhatsappElementEditor from "./elementEditors/FloatingWhatsappElementEditor";
import CustomEmbedElementEditor from "./elementEditors/CustomEmbedElementEditor";
import { resolveElementDescriptor } from "./elementEditing";
import { SEGMENT_LABELS } from "./segmentVisualStyle";

export default function LandingElementInspector({
  open,
  selectedElement,
  shellSectionId = "",
  form,
  baselineForm,
  saving,
  onClose,
  onCommit,
  onToggleSection,
  onUploadImage,
  onRemoveImage,
  onOpenGlobalPalette,
}) {
  const mobile = useMediaQuery("(max-width:760px)");
  const descriptor = React.useMemo(
    () => (selectedElement ? resolveElementDescriptor(form, selectedElement) : null),
    [form, selectedElement]
  );
  const sectionId = descriptor?.sectionId || shellSectionId;
  const sectionLabel = descriptor?.definition?.label || SEGMENT_LABELS[descriptor?.sectionType || sectionId] || "Sección";
  const elementLabel = descriptor?.label || "Sección";
  const isShell = !selectedElement && Boolean(shellSectionId);

  return (
    <Drawer
      anchor={mobile ? "bottom" : "right"}
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: mobile
          ? {
              width: "100%",
              maxWidth: "100%",
              height: "min(88dvh, calc(100dvh - env(safe-area-inset-top)))",
              maxHeight: "min(88dvh, calc(100dvh - env(safe-area-inset-top)))",
              borderRadius: "22px 22px 0 0",
              boxSizing: "border-box",
              overflowX: "hidden",
              pb: "env(safe-area-inset-bottom)",
              "& .MuiButton-root": { minHeight: 44 },
              "& .MuiIconButton-root": { minWidth: 44, minHeight: 44 },
              "& .MuiInputBase-root": { minHeight: 44 },
              "& .MuiFormControlLabel-root": { minHeight: 44 },
              "& .MuiFormControl-root, & .MuiTextField-root": { maxWidth: "100%" },
            }
          : { width: 410, top: 64, height: "calc(100% - 64px)" },
      }}
    >
      <Stack sx={{ minHeight: 0, height: "100%" }}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            px: 2,
            py: 1.25,
            borderBottom: "1px solid rgba(25,36,47,.09)",
            bgcolor: "#fff",
            position: "sticky",
            top: 0,
            zIndex: 2,
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="caption" sx={{ color: "rgba(25,36,47,.58)", fontWeight: 850 }}>
              {sectionLabel} &gt; {isShell ? "Sección" : elementLabel}
            </Typography>
            <Typography sx={{ mt: 0.25, fontWeight: 950, color: "#19242F" }}>
              {isShell ? "Ajustes de la sección" : `Editar ${String(elementLabel || "elemento").toLowerCase()}`}
            </Typography>
          </Box>
          {mobile ? (
            <Button
              variant="outlined"
              onClick={onClose}
              aria-label="Listo, volver a la página"
              sx={{
                flex: "0 0 auto",
                minHeight: 44,
                borderRadius: 999,
                px: 1.4,
                textTransform: "none",
                fontWeight: 950,
                color: "#19242F",
                borderColor: "rgba(25,36,47,.18)",
              }}
            >
              Listo
            </Button>
          ) : (
            <IconButton aria-label="Cerrar editor" onClick={onClose}><CloseRoundedIcon /></IconButton>
          )}
        </Stack>

        <Box sx={{ p: 2, overflowY: "auto", overflowX: "hidden", overscrollBehaviorY: "contain", flex: 1, minWidth: 0 }}>
          {isShell ? (
            <SectionShellEditor sectionId={shellSectionId} form={form} disabled={saving} onCommit={onCommit} onUploadImage={onUploadImage} onRemoveImage={onRemoveImage} onOpenGlobalPalette={onOpenGlobalPalette} />
          ) : descriptor?.kind === "button" ? (
            <ButtonElementEditor descriptor={descriptor} form={form} baselineForm={baselineForm} disabled={saving} onCommit={onCommit} />
          ) : descriptor?.kind === "image" ? (
            <ImageElementEditor descriptor={descriptor} form={form} baselineForm={baselineForm} disabled={saving} onCommit={onCommit} onUploadImage={onUploadImage} onRemoveImage={onRemoveImage} />
          ) : (descriptor?.kind === "collection-item" || descriptor?.kind === "collection-manager") && ["catalog.brands.items", "highlights"].includes(descriptor?.property?.path || descriptor?.collection?.property?.path) ? (
            <CollectionElementEditor descriptor={descriptor} form={form} baselineForm={baselineForm} disabled={saving} onCommit={onCommit} onUploadImage={onUploadImage} onRemoveImage={onRemoveImage} />
          ) : descriptor?.kind === "collection-item" || descriptor?.kind === "collection-manager" ? (
            <CollectionItemEditor descriptor={descriptor} form={form} baselineForm={baselineForm} disabled={saving} onCommit={onCommit} onUploadImage={onUploadImage} onRemoveImage={onRemoveImage} />
          ) : descriptor?.kind === "navbar-mobile" ? (
            <NavbarMobileElementEditor descriptor={descriptor} form={form} disabled={saving} onCommit={onCommit} />
          ) : descriptor?.kind === "card-system" ? (
            <CardSystemEditor descriptor={descriptor} form={form} baselineForm={baselineForm} disabled={saving} onCommit={onCommit} onUploadImage={onUploadImage} onRemoveImage={onRemoveImage} />
          ) : descriptor?.kind === "catalog-derived" || descriptor?.kind === "catalog-item-naming" ? (
            <CategoriesElementEditor
              descriptor={descriptor}
              form={form}
              disabled={saving}
              onCommit={onCommit}
              onToggleSection={onToggleSection}
              onUploadImage={onUploadImage}
              onRemoveImage={onRemoveImage}
            />
          ) : descriptor?.kind === "footer-group" ? (
            <FooterElementEditor
              descriptor={descriptor}
              form={form}
              baselineForm={baselineForm}
              disabled={saving}
              onCommit={onCommit}
            />
          ) : descriptor?.kind === "floating-whatsapp" ? (
            <FloatingWhatsappElementEditor
              descriptor={descriptor}
              form={form}
              disabled={saving}
              onCommit={onCommit}
            />
          ) : descriptor?.kind === "custom-embed" ? (
            <CustomEmbedElementEditor
              descriptor={descriptor}
              form={form}
              disabled={saving}
              onCommit={onCommit}
            />
          ) : descriptor?.kind === "contact-group" ? (
            <ContactElementEditor
              descriptor={descriptor}
              form={form}
              baselineForm={baselineForm}
              disabled={saving}
              onCommit={onCommit}
              onUploadImage={onUploadImage}
              onRemoveImage={onRemoveImage}
            />
          ) : descriptor?.kind === "text" && descriptor?.field ? (
            <TextElementEditor descriptor={descriptor} form={form} baselineForm={baselineForm} disabled={saving} onCommit={onCommit} />
          ) : (
            <Typography variant="body2" sx={{ color: "rgba(25,36,47,.7)" }}>
              Este componente usa la misma selección directa, pero sus controles específicos se habilitan al migrar su sección. No se creó un segundo campo de configuración.
            </Typography>
          )}
        </Box>
      </Stack>
    </Drawer>
  );
}
